import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Plus } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useClientActors } from '../../hooks/useActors';
import { Avatar } from '../tasks/linear/Avatar';
import { LinkClientDialog } from '../actors/LinkClientDialog';
import { SkeletonTable } from '../ui/Skeleton';

/**
 * Tab „Actori” on a client (Actori module): the actors used at this client,
 * with a warning on those who also appeared at a competitor, and „Adaugă
 * actor”, which runs the same conflict check as from the actor's page.
 */
export function ClientActorsTab({ client }: { client: { id: string; name: string } }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const { data: rows, isLoading } = useClientActors(client.id);
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {t('actors.client.tab')} · {rows?.length ?? 0}
        </h3>
        {can('actors', 'canEdit') && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Plus size={14} /> {t('actors.client.add')}
          </button>
        )}
      </div>
      {isLoading ? (
        <SkeletonTable />
      ) : !rows || rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          {t('actors.client.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-card">
          {rows.map(({ actor, link, conflicts }) => (
            <li key={actor.id} className="flex items-start gap-3 px-4 py-3">
              <Avatar name={actor.fullName} size={36} src={actor.avatarUrl} />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/actors/${actor.id}`}
                  className="font-medium text-brand-700 hover:underline"
                >
                  {actor.fullName}
                </Link>
                <p className="text-xs text-gray-500">
                  {t('actors.col.usedAt')}: {link.usedAt}
                  {link.note ? ` · ${link.note}` : ''}
                </p>
                {conflicts.length > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700">
                    <AlertTriangle size={12} />
                    {t('actors.client.alsoAt', {
                      clients: [...new Set(conflicts.map((c) => c.clientName))].join(', '),
                    })}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {adding && <LinkClientDialog client={client} onClose={() => setAdding(false)} />}
    </div>
  );
}
